import React, { useMemo, useState } from "react"
import { OverlayDrawer } from "packages/ui-shared/components/OverlayDrawer"
import { Header } from "./components/Header"
import {
  contentContainer,
  ctaWrapper,
  footnote,
  overlayWrapper,
  wrapper,
  list,
  termsLink,
} from "./FailedRenewalModal.css"
import { DeprecatedUISharedButton } from "../../../ui-shared/components/Button/Button"
import type { GracePeriodEntitlement } from "../../../../types"
import { ErrorAlert } from "../Alerts/ErrorAlert"
import { Link } from "../../../../components/Link"
import { useTrackElementClicked, useTrackElementViewed } from "../../tracking"
import { navigate } from "utils/routes"
import { useHideFailedRenewalModal } from "./useHideFailedRenewalModal"
import { durationToAsText } from "packages/membership/utils/duration-to-as-text"
import { useLocale } from "hooks"

type Props = {
  gracePeriodEntitlement: GracePeriodEntitlement
}

const defaultTrackingValues = {
  target: "failed_payment_modal",
  location: "failed_payment_modal",
}

const termsAndConditionsUrlPath = "legal/terms-and-conditions"

export const FailedRenewalModal: React.FC<Props> = ({
  gracePeriodEntitlement,
}) => {
  const { expiresAt, willBeGrantedGift } = gracePeriodEntitlement
  const { ref } = useTrackElementViewed(defaultTrackingValues)
  const { elementClicked } = useTrackElementClicked(defaultTrackingValues)
  const [isOpen, setIsOpen] = useState(true)
  const { hideModal } = useHideFailedRenewalModal()
  const locale = useLocale()

  const { duration, unit } = useMemo(
    () =>
      durationToAsText(expiresAt) ?? {
        duration: "",
        unit: "time",
      },
    [expiresAt]
  )

  return (
    <OverlayDrawer isOpen={isOpen} className={overlayWrapper}>
      <div className={wrapper} ref={ref}>
        <Header
          handleCloseModal={() => {
            setIsOpen(false)
            hideModal("MODAL_CLOSED")
            elementClicked({
              details: "close_icon",
            })
          }}
        />
        <ErrorAlert primaryMessage="Membership renewal failed" />
        <div className={contentContainer}>
          <div>
            Looks like there was an issue with your payment method. Update it
            now so you don't lose your Beauty Pie perks!
          </div>
          <br />
          <ul className={list}>
            <li>
              Unlock <strong>Member Prices</strong>
            </li>
            <li>
              Keep your <strong>exclusive Member Perks</strong>
            </li>
            {willBeGrantedGift && (
              <li>
                Claim your FREE <strong>Gift</strong>
              </li>
            )}
          </ul>
        </div>
        <div>
          <div className={footnote}>
            You have{" "}
            <strong>
              {duration} {unit}
            </strong>{" "}
            left to{" "}
            {willBeGrantedGift ? (
              <span>
                unlock your <strong>free gift</strong> when you{" "}
              </span>
            ) : (
              <></>
            )}
            renew your membership.{" "}
            <Link
              href={`/${termsAndConditionsUrlPath}`}
              className={termsLink}
              onClick={() => {
                const url = [
                  locale === "en-US" ? "/us/" : "/",
                  termsAndConditionsUrlPath,
                ].join("")

                elementClicked({
                  details: "terms_and_conditions",
                  contentUrl: `${window.location.origin}${url}`,
                })
              }}
            >
              T&Cs Apply
            </Link>
          </div>
          <div className={ctaWrapper}>
            <DeprecatedUISharedButton
              variant="primary"
              type="button"
              fullWidth
              handleClick={() => {
                const url = [
                  locale === "en-US" ? "/us/" : "/",
                  "account/my-membership/update-payment-details?onCompletion=reactivate",
                ].join("")

                elementClicked({
                  locationDetails: "navigate_to_update_payment_details",
                  details: "update_payment_details_and_renew",
                  contentUrl: `${window.location.origin}${url}`,
                })
                navigate(
                  "/account/my-membership/update-payment-details?onCompletion=reactivate"
                )
                hideModal("NAVIGATE_TO_PAYMENT")
              }}
            >
              Update Payment Details & Renew
            </DeprecatedUISharedButton>
          </div>
        </div>
      </div>
    </OverlayDrawer>
  )
}
